import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { InvoiceSettingsService, InvoiceSettings, defaultInvoiceSettings } from '../services/invoiceSettingsService';
import { DEFAULT_TERM_OPTIONS } from '../lib/invoices/invoiceTerms';
import { useBusiness } from '../contexts/BusinessContext';
import { useModalZIndex } from '../contexts/ModalStackContext';

interface InvoiceSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSettingsChange?: (settings: InvoiceSettings) => void;
  currentSettings?: InvoiceSettings | null;
}

export default function InvoiceSettingsModal({
  isOpen,
  onClose,
  onSettingsChange,
  currentSettings
}: InvoiceSettingsModalProps) {
  const { selectedBusiness } = useBusiness();
  const stackZ = useModalZIndex(100000);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [settings, setSettings] = useState<Partial<InvoiceSettings>>({});

  // Detect mobile
  const isMobile = window.innerWidth < 768;

  // Get business logo from business settings
  const businessLogoUrl = selectedBusiness?.booking_settings?.logo_url || null;

  // Load settings when modal opens
  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      if (currentSettings) {
        setSettings(currentSettings);
      } else if (selectedBusiness?.id) {
        // Load from database or use defaults
        loadSettings();
      }
    }
  }, [isOpen, currentSettings, selectedBusiness?.id]);

  const loadSettings = async () => {
    if (!selectedBusiness?.id) return;
    
    try {
      const loadedSettings = await InvoiceSettingsService.getInvoiceSettings(selectedBusiness.id);
      if (loadedSettings) {
        setSettings(loadedSettings);
      } else {
        // Use defaults with business info
        const defaultSettings: Partial<InvoiceSettings> = {
          business_id: selectedBusiness.id,
          company_name: selectedBusiness.name || '',
          company_address: selectedBusiness.address || '',
          company_phone: selectedBusiness.phone || '',
          company_email: selectedBusiness.email || '',
          company_website: '',
          ...defaultInvoiceSettings
        };
        setSettings(defaultSettings);
      }
    } catch (error) {
      console.error('Error loading invoice settings:', error);
      // Use defaults
      const defaultSettings: Partial<InvoiceSettings> = {
        business_id: selectedBusiness.id,
        company_name: selectedBusiness.name || '',
        company_address: selectedBusiness.address || '',
        company_phone: selectedBusiness.phone || '',
        company_email: selectedBusiness.email || '',
        company_website: '',
        ...defaultInvoiceSettings
      };
      setSettings(defaultSettings);
    }
  };

  const handleSave = async () => {
    if (!selectedBusiness?.id) return;
    
    setIsSaving(true);
    try {
      const success = await InvoiceSettingsService.saveInvoiceSettings(
        selectedBusiness.id,
        settings
      );
      
      if (success) {
        // Reload to get the saved settings
        const savedSettings = await InvoiceSettingsService.getInvoiceSettings(selectedBusiness.id);
        if (savedSettings && onSettingsChange) {
          onSettingsChange(savedSettings);
        }
        onClose();
      }
    } catch (error) {
      console.error('Error saving invoice settings:', error);
    } finally {
      setIsSaving(false);
    }
  };


  const handleChange = (key: keyof InvoiceSettings, value: any) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  if (!isOpen) return null;

  const modalContent = (
    <>
      {/* Backdrop with blur - iOS style */}
      <div
        className="fixed inset-0 bg-black/20 backdrop-blur-sm"
        onClick={onClose}
        style={{
          zIndex: stackZ,
          animation: isAnimating ? 'fadeIn 0.2s ease-out' : 'none'
        }}
      />

      {/* Modal Content - Full Screen iOS Style on Mobile, Right Side on Desktop */}
      <div
        className={`fixed bg-white transform transition-transform duration-300 ease-out ${
          isMobile
            ? `inset-0 ${isOpen ? 'translate-y-0' : 'translate-y-full'}`
            : `top-0 right-0 h-full ${isOpen ? 'translate-x-0' : 'translate-x-full'}`
        }`}
        style={{
          zIndex: stackZ + 1,
          width: isMobile ? '100vw' : '672px',
          boxShadow: isMobile ? '0 -4px 20px rgba(0, 0, 0, 0.15)' : '-4px 0 20px rgba(0, 0, 0, 0.15)',
          paddingTop: 'env(safe-area-inset-top, 0px)',
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
          paddingLeft: 'env(safe-area-inset-left, 0px)',
          paddingRight: 'env(safe-area-inset-right, 0px)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* iOS-style drag handle - only on mobile */}
        {isMobile && (
          <div className="flex justify-center pt-3 pb-2">
            <div className="w-12 h-1 bg-gray-300 rounded-full"></div>
          </div>
        )}

        <div className="flex flex-col h-full" style={{ height: isMobile ? 'calc(100vh - 20px)' : '100vh' }}>
          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute right-4 h-8 w-8 flex items-center justify-center text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 active:bg-gray-200 active:scale-95 transition-all duration-150 touch-manipulation z-10"
            style={{ WebkitTapHighlightColor: 'transparent', top: 'calc(env(safe-area-inset-top, 0px) + 16px)' }}
          >
            <X className="h-5 w-5" />
          </button>

          {/* Header */}
          <div className="bg-gray-50 border-b border-gray-200 px-6 py-6 text-center">
            <h2 className="text-2xl font-bold text-gray-900" style={{ 
              letterSpacing: '-0.02em'
            }}>
              Invoice Settings
            </h2>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto bg-white" style={{ WebkitOverflowScrolling: 'touch' }}>
            <div className="px-6 py-6 space-y-6">
              {/* Company Information Section */}
              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-4">Company Information</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Company Name
                    </label>
                    <input
                      type="text"
                      value={settings.company_name || ''}
                      onChange={(e) => handleChange('company_name', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Your Business Name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Address
                    </label>
                    <textarea
                      value={settings.company_address || ''}
                      onChange={(e) => handleChange('company_address', e.target.value)}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="123 Business Street&#10;City, State 12345"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        Phone
                      </label>
                      <input
                        type="text"
                        value={settings.company_phone || ''}
                        onChange={(e) => handleChange('company_phone', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="(555) 123-4567"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        Email
                      </label>
                      <input
                        type="email"
                        value={settings.company_email || ''}
                        onChange={(e) => handleChange('company_email', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="info@yourbusiness.com"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Website
                    </label>
                    <input
                      type="text"
                      value={settings.company_website || ''}
                      onChange={(e) => handleChange('company_website', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="www.yourbusiness.com"
                    />
                  </div>
                </div>
              </div>

              {/* Logo Section */}
              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-4">Logo</h3>
                <div className="space-y-4">
                  {businessLogoUrl ? (
                    <div className="space-y-3">
                      <div className="w-32 h-32 border-2 border-gray-200 rounded-lg overflow-hidden bg-gray-50 flex items-center justify-center">
                        <img
                          src={businessLogoUrl}
                          alt="Company Logo"
                          className="w-full h-full object-contain"
                        />
                      </div>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={settings.include_logo ?? false}
                          onChange={(e) => handleChange('include_logo', e.target.checked)}
                          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <span className="ml-2 text-sm text-gray-700">Include logo on invoices</span>
                      </label>
                      <p className="text-xs text-gray-500">
                        Logo is managed in Business Settings
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div className="w-32 h-32 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-gray-50">
                        <span className="text-sm text-gray-400">No logo set</span>
                      </div>
                      <p className="text-xs text-gray-500">
                        Upload a logo in Business Settings to use it on invoices
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Invoice Options Section */}
              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-4">Invoice Options</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Invoice Prefix
                    </label>
                    <input
                      type="text"
                      value={settings.invoice_prefix || 'INV-'}
                      onChange={(e) => handleChange('invoice_prefix', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="INV-"
                      maxLength={10}
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Invoice number will be: {settings.invoice_prefix || 'INV-'}1234
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Payment Terms
                    </label>
                    <select
                      value={settings.payment_terms || 'On receipt'}
                      onChange={(e) => handleChange('payment_terms', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="On receipt">On receipt</option>
                      <option value="Due within 30 days">Due within 30 days</option>
                      <option value="Due within 90 days">Due within 90 days</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Default Due Date
                    </label>
                    <select
                      value={settings.default_payment_terms || 'due_on_receipt'}
                      onChange={(e) => handleChange('default_payment_terms', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      {DEFAULT_TERM_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      Sets an invoice's due date when it's issued (booking marked complete). Can be overridden per invoice.
                    </p>
                  </div>

                  <div className="space-y-3">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={settings.show_item_descriptions ?? true}
                        onChange={(e) => handleChange('show_item_descriptions', e.target.checked)}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">Show service descriptions</span>
                    </label>

                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={settings.show_vehicle ?? true}
                        onChange={(e) => handleChange('show_vehicle', e.target.checked)}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">Show vehicle information</span>
                    </label>

                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={settings.show_tax_breakdown ?? true}
                        onChange={(e) => handleChange('show_tax_breakdown', e.target.checked)}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">Show tax breakdown</span>
                    </label>
                  </div>
                </div>
              </div>

            </div>
          </div>

          {/* Footer */}
          <div className="border-t border-gray-200 px-6 py-4 bg-gray-50">
            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="flex-1 px-4 py-2.5 border border-gray-300 rounded-xl text-gray-700 font-medium hover:bg-gray-50 active:bg-gray-100 transition-colors touch-manipulation"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="flex-1 px-4 py-2.5 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 active:bg-blue-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                {isSaving ? 'Saving...' : 'Save Settings'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );

  return createPortal(modalContent, document.body);
}

